import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { filesApi } from '../utils/api';

const UsageStats = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await filesApi.getUsageStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch usage stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/20 p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!stats) {
    return null;
  }

  const isTrial = stats.trial?.is_trial;
  const trialEndDate = stats.trial?.trial_end_date ? new Date(stats.trial.trial_end_date) : null;

  return (
    <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/20 p-6">
      <h2 className="text-xl font-bold text-neutral-800 mb-6 flex items-center">
        <BarChart3 className="h-6 w-6 mr-3 text-primary-500" />
        {t('usage.title')}
      </h2>

      <div className="space-y-4">
        {/* Trial Status for Trial Users */}
        {isTrial && (
          <div className="bg-gradient-to-r from-warning-50 to-orange-50 rounded-xl p-4 border border-warning-300">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-warning-800">{t('usage.trialStatus')}</h3>
              <span className="bg-warning-200 text-warning-800 text-xs px-2 py-1 rounded-full font-semibold">
                {t('usage.trial')}
              </span>
            </div>

            <div className="space-y-3">
              {/* Trial Progress Bar */}
              <div>
                <div className="flex justify-between text-xs text-neutral-700 mb-1">
                  <span>{t('usage.rowsUsed')}</span>
                  <span className="font-bold">
                    {stats.trial.rows_used || 0} / {stats.trial.rows_limit || 0}
                  </span>
                </div>
                <div className="w-full bg-neutral-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${
                      (stats.trial.rows_used / stats.trial.rows_limit) > 0.9
                        ? 'bg-error-500'
                        : (stats.trial.rows_used / stats.trial.rows_limit) > 0.7
                        ? 'bg-warning-500'
                        : 'bg-success-500'
                    }`}
                    style={{ width: `${Math.min(100, (stats.trial.rows_used / stats.trial.rows_limit) * 100)}%` }}
                  ></div>
                </div>
                <p className="text-xs text-neutral-600 mt-1">
                  {stats.trial.rows_remaining || 0} {t('usage.rowsRemaining')}
                </p>
              </div>

              {/* Trial End Date */}
              {trialEndDate && (
                <div className="bg-white/60 rounded-lg p-3">
                  <p className="text-xs text-neutral-600 mb-1">{t('usage.trialEndsOn')}</p>
                  <p className="text-sm font-bold text-warning-700">
                    {trialEndDate.toLocaleDateString()} ({Math.max(0, Math.ceil((trialEndDate - new Date()) / (1000 * 60 * 60 * 24)))} {t('usage.daysLeft')})
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Current Month Stats (for non-trial users or as additional info for trial users) */}
        {!isTrial && (
          <div className="bg-gradient-to-r from-primary-50 to-blue-50 rounded-xl p-4 border border-primary-200">
            <div className="flex items-center mb-3">
              <TrendingUp className="h-5 w-5 text-primary-600 mr-2" />
              <h3 className="text-sm font-semibold text-neutral-800">{t('usage.monthlyTitle')}</h3>
            </div>
            <p className="text-xs text-neutral-600 mb-4">
              {t('usage.since')} {new Date(stats.current_year, stats.current_month - 1, 1).toLocaleDateString()}
            </p>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/60 rounded-lg p-3">
                <p className="text-xs text-neutral-600 mb-1">{t('usage.requests')}</p>
                <p className="text-2xl font-bold text-primary-600">{stats.current_month_requests || 0}</p>
              </div>
              <div className="bg-white/60 rounded-lg p-3">
                <p className="text-xs text-neutral-600 mb-1">{t('usage.rows')}</p>
                <p className="text-2xl font-bold text-success-600">{stats.current_month_rows || 0}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsageStats;
